import { useMemo, useRef, useState } from 'react';
import { useDrag } from 'react-dnd';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import DischargeModal from 'components/modals/DischargeModal';
import CardBadge from 'components/shared/CardBadge';
import Tooltip from 'components/shared/Tooltip';
import Episode from 'models/Episode';
import { SupplementalEpisodeInformation } from 'models/Episode';
import LocationEpisode from 'models/LocationEpisode';
import Profile, { PERMISSIONS } from 'models/Profile';
import { colors } from 'styles/theme/colors';
import { Label, LabelBold, LabelSmall } from 'styles/typography';
import DischargeIndicator from 'svg/DischargeIndicator';
import GroupIcon from 'svg/GroupIcon';
import NoteIcon from 'svg/NoteIcon';
import PriorityNoteFlag from 'svg/PriorityNoteFlag';
import RefusalCardIcon from 'svg/RefusalCardIcon';
import SafeReturnIcon from 'svg/SafeReturnIcon';
import StarIcon from 'svg/StarIcon';
import WarnIcon from 'svg/WarnIcon';

import { SkeletonBlurb } from '../Containers';

const CardBanner = styled.div<{ variant: 'green' | 'red' }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 22px;
  background: ${({ variant }) => (variant === 'green' ? colors.accentGreen : colors.accentRed)};
  color: ${() => colors.white};
  border-radius: 6px 6px 0 0;
  & svg {
    margin-right: 5px;
  }
`;

const LOSBadge = styled.div<{ patientOnTrack: string | null }>`
  border: 2px solid transparent;
  border-radius: 50%;
  height: 24px;
  width: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ patientOnTrack }) => {
    if (!patientOnTrack) return colors.black05;

    return {
      Yes: colors.accentGreen,
      Unsure: colors.accentYellow,
      No: colors.accentRed,
    }[patientOnTrack];
  }};
`;
const LOSNumber = styled(LabelBold)<{ lengthOfStay: number; patientOnTrack: string | null }>`
  color: ${({ patientOnTrack }) =>
    patientOnTrack && ['No', 'Yes'].includes(patientOnTrack) ? colors.white : colors.black};
  font-size: ${({ lengthOfStay }) => (lengthOfStay >= 1000 ? 10 : 12)}px;
`;

const EmptyPatientName = styled(SkeletonBlurb)`
  width: 100%;
  height: 16px;
  margin-bottom: 4px;
`;
const EmptyPatientPlanType = styled(SkeletonBlurb)`
  height: 14px;
  width: 80px;
`;
const EmptyBadge = styled(SkeletonBlurb)`
  border: 2px solid transparent;
  border-radius: 50%;
  height: 24px;
  width: 24px;
`;
const EmptyRehabFacilityContainer = styled(SkeletonBlurb)`
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  width: 120px;
  margin-top: 15px;
`;

export function SkeletonPatientCard(_props: object) {
  return (
    <CardBody unacknowledgedEscalations={0} unacknowledgedPriorityNotes={0}>
      <Content indicateContinuedCare={false} unacknowledgedEscalations={0} unacknowledgedPriorityNotes={0}>
        <TopInfoContainer>
          <PatientInfoContainer>
            <EmptyPatientName />
            <EmptyPatientPlanType />
          </PatientInfoContainer>
          <EmptyBadge />
        </TopInfoContainer>
        <BottomInfoContainer>
          <EmptyRehabFacilityContainer />
        </BottomInfoContainer>
      </Content>
    </CardBody>
  );
}

type PortfolioCardType = {
  locationEpisode: LocationEpisode;
  info: SupplementalEpisodeInformation;
  profile: Profile;
  invalidateColumn: () => void;
  onCardClick?: () => void;
};
export default function DraggablePatientCard(props: PortfolioCardType) {
  const {
    profile,
    locationEpisode,
    info: { hasActiveServiceRefusals },
    invalidateColumn,
  } = props;
  const { id, group, currentRehabState: rehabState } = locationEpisode;

  const rehabFacilityId = group?.id;

  const isMobile = useMediaQuery({
    query: '(max-width: 800px)',
  });
  const isDraggable =
    !isMobile &&
    !hasActiveServiceRefusals &&
    profile.hasAny([PERMISSIONS.rehabStateAdmissionCreate, PERMISSIONS.rehabStateDischargeCreate], {
      locationEpisode: new LocationEpisode({ id: locationEpisode.id, groupId: rehabFacilityId }),
      episode: new Episode({ id: locationEpisode.episodeId, owner: locationEpisode.owner }),
    });

  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, dragSource] = useDrag<any, any, { isDragging: boolean }>({
    item: {
      type: 'card',
      locationEpisode,
      rehabState,
      invalidateColumn,
    },
    type: 'card',
    canDrag: () => !!isDraggable,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  dragSource(ref);

  return (
    <DragContainer
      key={id}
      ref={ref}
      data-id={id}
      data-dd-action-name={`Patient card: ${id}`}
      style={{ opacity: isDragging ? 0 : 1 }}
      draggable={isDraggable}>
      <PatientCard {...props} />
    </DragContainer>
  );
}

export function PatientCard({
  profile,
  locationEpisode,
  onCardClick,
  info: {
    hasActiveServiceRefusals,
    hasActiveSafeReturn,
    hasNewChanges,
    latest,
    onTrack,
    planTypeClassification,
    lengthOfStay,
    statusOutOfDate,
    unacknowledgedEscalations,
    unacknowledgedPriorityNotes,
    removedFromCensus,
  },
}: PortfolioCardType) {
  const {
    patient: { name },
    trackLos,
    currentRehabState: rehabState,
    owner: { clientId: ownerClientId, clientName: ownerClientName },
    group,
    episodeId,
  } = locationEpisode;

  const [showDischargeModal, setShowDischargeModal] = useState(false);
  const queryClient = useQueryClient();

  const rehabType = group?.groupType?.displayName;
  const rehabFacility = group?.name;

  const days = trackLos ? lengthOfStay : null;
  const showLengthOfStayBadge = typeof days === 'number' || (onTrack && rehabState.inTreatment);
  const groupTypeId = group?.groupType?.id;
  const showRemovedFromCensus =
    removedFromCensus &&
    ownerClientId === profile.actingClient.id &&
    !rehabState.discharged &&
    !!groupTypeId &&
    profile.actingClient.managesAndProvidesGroupType(groupTypeId);
  const patientOnTrack = useMemo(() => {
    return !rehabState.inTreatment || statusOutOfDate ? null : onTrack;
  }, [onTrack, rehabState, statusOutOfDate]);
  const navigate = useNavigate();

  return (
    <>
      {/* Only show one banner - safe return will take priority */}
      {!!unacknowledgedEscalations && !hasActiveSafeReturn && (
        <CardBanner variant='red' data-cy='escalationHeader'>
          <WarnIcon color={colors.white} />
          <Label>Escalation</Label>
        </CardBanner>
      )}
      {hasActiveSafeReturn && (
        <CardBanner variant='green'>
          <SafeReturnIcon />
          <Label>Safe Return Requested</Label>
        </CardBanner>
      )}
      <CardBody
        data-cy='cardBody'
        onClick={() => {
          if (onCardClick) {
            onCardClick();
          } else {
            navigate(`/patients/${episodeId}`);
          }
        }}
        unacknowledgedEscalations={unacknowledgedEscalations}
        unacknowledgedPriorityNotes={unacknowledgedPriorityNotes}>
        <Content
          indicateContinuedCare={!latest}
          unacknowledgedEscalations={unacknowledgedEscalations}
          unacknowledgedPriorityNotes={unacknowledgedPriorityNotes}>
          <TopInfoContainer>
            <PatientInfoContainer>
              <PatientName>
                {showRemovedFromCensus && (
                  <IconContainer
                    data-cy='dischargeIndicatorIcon'
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDischargeModal(true);
                    }}>
                    <Tooltip text='This patient has been identified in a census update as ready to discharge in Olio'>
                      <DischargeIndicator />
                    </Tooltip>
                  </IconContainer>
                )}
                {name}
              </PatientName>
              <PatientPlanType>{planTypeClassification?.name || '—'}</PatientPlanType>
            </PatientInfoContainer>
            {showLengthOfStayBadge && (
              <LOSBadge data-cy='losbadge' patientOnTrack={patientOnTrack}>
                <LOSNumber lengthOfStay={lengthOfStay} patientOnTrack={patientOnTrack}>
                  {lengthOfStay}
                </LOSNumber>
              </LOSBadge>
            )}
          </TopInfoContainer>
          <BottomInfoContainer>
            <CardBadge
              text={`${rehabType} - ${rehabFacility}`}
              renderIcon={() => <GroupIcon color={colors.black} />}
              dataCy='groupBadge'
            />
            {ownerClientId !== profile.actingClient.id && (
              <CardBadge
                dataCy='ownerClientBadge'
                $backgroundColor={'#E7F5FB'}
                text={ownerClientName}
                renderIcon={() => <StarIcon color={colors.black} />}
              />
            )}
            {!!unacknowledgedPriorityNotes && (
              <CardBadge
                dataCy='priorityNoteBadge'
                text={'Priority'}
                $backgroundColor={colors.accentRed10}
                $color={colors.accentRed}
                renderIcon={() => <PriorityNoteFlag />}
              />
            )}
            {hasNewChanges && <CardBadge renderIcon={() => <NoteIcon color={colors.primaryBlue} />} />}
            {hasActiveServiceRefusals && (
              <CardBadge
                text='Refusal'
                $backgroundColor={colors.black75}
                $color={colors.white}
                renderIcon={() => <RefusalCardIcon />}
              />
            )}
          </BottomInfoContainer>
        </Content>
      </CardBody>
      {showDischargeModal && (
        <DischargeModal
          invalidateData={() => {
            queryClient.invalidateQueries({
              queryKey: ['portfolio'],
            });
          }}
          episode={new Episode({ id: locationEpisode.episodeId })}
          locationEpisode={locationEpisode}
          setShow={() => setShowDischargeModal(false)}
          patientName={locationEpisode.patient.name ?? ''}
        />
      )}
    </>
  );
}

const DragContainer = styled.div`
  ${(props) => (props.draggable ? 'cursor: move; cursor: grab;' : 'cursor: pointer;')};
`;

const CardBody = styled.div<{
  unacknowledgedEscalations: number;
  unacknowledgedPriorityNotes: number;
}>`
  cursor: pointer;
  border: ${({ unacknowledgedPriorityNotes }) =>
    unacknowledgedPriorityNotes ? `1.5px solid ${colors.accentRed}` : null};
  border-radius: ${({ unacknowledgedEscalations }) => (unacknowledgedEscalations ? '0 0 6px 6px' : '6px')};
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  background: ${() => colors.white};
`;

const Content = styled.div<{
  indicateContinuedCare: boolean;
  unacknowledgedEscalations: number;
  unacknowledgedPriorityNotes: number;
}>`
  padding: 10px 8px 10px 12px;
  border-left: ${({ indicateContinuedCare }) => (indicateContinuedCare ? `5px solid ${colors.accentGreen}` : null)};
  border-radius: ${({ unacknowledgedPriorityNotes, unacknowledgedEscalations }) =>
    unacknowledgedPriorityNotes || unacknowledgedEscalations ? '0 0 6px 6px' : '6px'};
`;

const TopInfoContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const BottomInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 4px;
`;

const PatientInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 80%;
  width: 100%;
`;

const PatientName = styled(LabelBold)`
  display: flex;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PatientPlanType = styled(LabelSmall)`
  color: ${() => colors.black75};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconContainer = styled.div`
  margin-right: 8px;

  &:hover {
    cursor: pointer;
  }
`;
